import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { IFormInputMap } from "pages/CreateRemit/Index";
import { ControllerRenderProps, FieldValues, Path } from "react-hook-form";
import ReactSelect from "react-select";
import { useCallback } from "react";
import { Switch } from "@/components/ui/switch";

export interface IField<T extends FieldValues> {
  field: ControllerRenderProps<T, Path<T>>;
}
interface ICurrencyInputProps<T extends FieldValues = IFormInputMap> {
  currencyType: string;
  currencySymbol?: string;
  field: IField<T>["field"];
  disabled?: boolean;
  isLoading?: boolean;
}

interface IRadioInputProps<T extends FieldValues = IFormInputMap> {
  options: { label: string; value: string }[];
  field: IField<T>["field"];
  defaultValue: string;
}

interface ISelectInputProps<T extends FieldValues = IFormInputMap> {
  name: string;
  field: IField<T>["field"];
  options: { label: string; value: string }[];
  isDisabled?: boolean;
  isClearable?: boolean;
  isSearchable?: boolean;
  placeholder?: string;
  value?: { label: string; value: string };
  onChange?: (value: { label: string; value: string }) => void;
}

const CurrencyInput = <T extends FieldValues = IFormInputMap>({
  currencyType = "INR",
  currencySymbol = "",
  field,
  isLoading = false,  
}: ICurrencyInputProps<T>) => {
  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLInputElement>) => e.currentTarget.blur(),
    [],
  );

  return (
    <div className="">
      <div className="relative w-full rounded-md">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5">
          <span className="text-gray-500 sm:text-sm">{currencySymbol}</span>
          <span className=" font-medium leading-4 -tracking-[1%] p-2.5 rounded-md bg-[#E1EBFC] text-blue-700 sm:text-sm">
            {currencyType}
          </span>
        </div>
        <Input
          type="number"
          {...field}
          value={field.value as number}
          disabled={isLoading || field.disabled}
          onWheel={handleWheel}
          className="m-0 h-12 pl-[70px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg"
        />
        {isLoading && (
          <span
            className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5"
            role="status"
          >
            <svg
              aria-hidden="true"
              className="inline w-4 h-4 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
            <span className="sr-only">Loading...</span>
          </span>
        )}
      </div>
    </div>
  );
};

const RadioInput = <T extends FieldValues = IFormInputMap>({
  field,
  options,
  defaultValue,
}: IRadioInputProps<T>) => {
  return (
    <RadioGroup
      className="flex gap-3 w-full"
      orientation="horizontal"
      value={field.value || defaultValue}
      onValueChange={field.onChange}
    >
      {options.map((option) => (
        <Label
          htmlFor={option.value}
          key={crypto.randomUUID()}
          className={`flex h-[46px] w-full items-center p-2.5 rounded-md border justify-between cursor-pointer ${
            (field.value ? field.value : defaultValue) === option.value
              ? "border-blue-700"
              : "border-gray-border-gray-lite"
          }`}
        >
          <RadioGroupItem
            className={`${
              (field.value ? field.value : defaultValue) === option.value
                ? "text-transparent ring-4 ring-blue-700 ring-offset-0"
                : "bg-white-A700 ring-gray-border-gray-lite text-transparent ring-4 ring-offset-0"
            } rounded-full`}
            onClick={() => field.onChange(option.value)}
            value={option.value}
            id={option.value}
          />
          <div className="cursor-pointer">{option.label}</div>
        </Label>
      ))}
    </RadioGroup>
  );
};

const FileInput = () => {
  return (
    <div className="relative mt-2 rounded-md shadow-sm">
      <Input
        type="file"
        className="m-0 h-12 max-w-[340px] pl-[70px] border border-gray-border-gray-lite py-1.5 pr-[7px] rounded-lg"
      />
    </div>
  );
};

const SelectInput = <T extends FieldValues = IFormInputMap>({
  field,
  placeholder,
  isClearable = false,
  isDisabled,
  isSearchable = true,
  options,
  name,
}: ISelectInputProps<T>) => {
  return (
    <ReactSelect
      className=""
      name={name}
      styles={{
        control: (provided, state) => ({
          ...provided,
          zIndex: 0,
          borderRadius: "0.5rem",
          height: "3rem",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderColor: state.isFocused ? "#165FE3" : "#d2d2d2",
          boxShadow: "none",
        }),
      }}
      isClearable={isClearable}
      isDisabled={isDisabled}
      isSearchable={isSearchable}
      placeholder={placeholder}
      options={options}
      onChange={(value) => {
        field.onChange(value?.value);
      }}
      value={options.find((option) => option.value === field.value)}
    />
  );
};

const SwitchInput = ({ field }: IField<IFormInputMap>) => (
  <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
);

export { CurrencyInput, RadioInput, FileInput, SelectInput, SwitchInput };
